import { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { authFetchJson } from "@/lib/api";
import { CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CS_COLORS } from "@/lib/chartTheme";
import { pct } from "./format";
import { Card, ErrorBanner, StatCard } from "./ui";

type Overview = {
  days: number;
  total_logins: number;
  password_reset_count: number;
  active_users_30d: number;
  logins_by_surface: Record<string, number>;
  events_by_day: { date: string; count: number }[];
  top_users: { user_id: string; email: string; logins: number; last_seen: string | null }[];
};

export function LoginsSection({ days, onUserClick }: { days: number; onUserClick: (userId: string, email: string) => void }) {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetchJson<Overview>(`/api/admin/analytics/overview?days=${String(days)}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const surfaceTotal = useMemo(
    () => (data ? Object.values(data.logins_by_surface).reduce((a, b) => a + b, 0) : 0),
    [data],
  );

  return (
    <section className="space-y-4">
      <p className="cs-caption">Aanmeldingen</p>
      {error ? <ErrorBanner message={`Kon aanmeldingen niet laden: ${error}`} /> : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard label="Aanmeldingen" value={data?.total_logins ?? 0} loading={loading} />
        <StatCard label="Actieve gebruikers (server)" value={data?.active_users_30d ?? 0} loading={loading} />
        <StatCard label="Herstelcodes gebruikt" value={data?.password_reset_count ?? 0} loading={loading} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card caption="Gebeurtenissen per dag" className="lg:col-span-2">
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.events_by_day ?? []}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="date" {...CHART_AXIS} />
                <YAxis {...CHART_AXIS} />
                <Tooltip {...CHART_TOOLTIP} />
                <Area type="monotone" dataKey="count" stroke={CS_COLORS.rose} fill={CS_COLORS.rose} fillOpacity={0.12} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card caption="Aanmeldingen per platform">
          <ul className="space-y-2">
            {(["mobile", "web", "widget", "unknown"] as const).map((k) => {
              const v = data?.logins_by_surface[k] ?? 0;
              const p = pct(v, surfaceTotal);
              return (
                <li key={k}>
                  <div className="mb-1 flex justify-between text-xs text-[var(--cs-ink-muted)]">
                    <span className="capitalize">{k}</span>
                    <span>{p}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-[var(--cs-ivory)]">
                    <div className="h-full rounded-full bg-[var(--cs-rose)]" style={{ width: `${String(p)}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
      <Card caption={`Top gebruikers (laatste ${String(days)} dagen)`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--cs-ink-muted)]">
              <th className="py-2 font-medium">E-mail</th>
              <th className="py-2 font-medium text-right">Logins</th>
              <th className="py-2 font-medium">Laatst gezien</th>
            </tr>
          </thead>
          <tbody>
            {(data?.top_users ?? []).map((u) => (
              <tr
                key={u.user_id}
                onClick={() => { onUserClick(u.user_id, u.email); }}
                className="cursor-pointer border-t border-[rgba(10,52,96,0.06)] hover:bg-[var(--cs-ivory)]"
              >
                <td className="py-2 text-[var(--cs-navy)]">{u.email || u.user_id.slice(0, 8)}</td>
                <td className="py-2 text-right tabular-nums">{u.logins}</td>
                <td className="py-2 text-[var(--cs-ink-muted)] tabular-nums">{u.last_seen ?? "—"}</td>
              </tr>
            ))}
            {!loading && (data?.top_users ?? []).length === 0 ? (
              <tr><td colSpan={3} className="py-6 text-center text-[var(--cs-ink-muted)]">Geen activiteit in deze periode</td></tr>
            ) : null}
          </tbody>
        </table>
      </Card>
    </section>
  );
}
